import { PatientTrackLabel, Track, TrackLabel } from '@bariendo/lib/constants';
import { Badge } from '@mantine/core';

// Warning-amber shared by all B2B partner tracks (Carrum, Transcarent, …) so
// they read as one B2B family, distinct from the categorical activity colors
// elsewhere in Orbit. Self-pay (D2C) uses the neutral pill chrome. Per BAR-2341.
const B2B_AMBER = '#D89614';

// Lavender for the Insurance track: not an employer benefit, so it stays out of
// the B2B amber family. The one categorical color no procedure uses (P-BAR-63).
const INSURANCE_LAVENDER = '#7E6CB8';

// The Intake hub prototype's smaller, sentence-case pill.
const QUEUE_ROOT = {
  height: 'auto',
  padding: '1px 9px',
  fontSize: 11,
  lineHeight: '16px',
  fontWeight: 500,
  textTransform: 'none',
} as const;

/**
 * Go-to-market / track pill for the patient profile header. Renders the
 * patient's track (resolved from their active EpisodeOfCare via BAR-2335):
 * self-pay is neutral (matches the state/language pills); insurance is
 * lavender; partner tracks share the B2B amber fill.
 *
 * Renders nothing when the track is unresolved (no active EpisodeOfCare) — we do
 * NOT default to "Self-pay", so an un-backfilled patient (incl. a manually
 * onboarded Carrum patient, BAR-2322) is never mislabeled. The pill appears once
 * the patient has a real track.
 */
export function TrackPill({ track, variant }: { track: Track | undefined; variant?: 'queue' }) {
  if (!track) {
    return null;
  }

  // A queue draws every employer-sponsored patient as `employer`, partners
  // included, so only the header can call that track unverified (BAR-2992).
  const label = variant === 'queue' ? TrackLabel[track] : PatientTrackLabel[track];

  const queue = variant === 'queue' ? QUEUE_ROOT : undefined;

  if (track === Track.D2C) {
    return (
      <Badge
        size="sm"
        color="gray"
        variant="light"
        radius="xl"
        styles={queue && { root: { ...queue, backgroundColor: 'rgba(0, 0, 0, 0.06)', color: 'rgba(0, 0, 0, 0.55)' } }}
      >
        {label}
      </Badge>
    );
  }

  return (
    <Badge
      size="sm"
      variant="filled"
      radius="xl"
      styles={{
        root: {
          ...queue,
          backgroundColor: track === Track.INSURANCE ? INSURANCE_LAVENDER : B2B_AMBER,
          color: '#fafcf7',
        },
      }}
    >
      {label}
    </Badge>
  );
}
